import { ActionIcon, Avatar, Badge, Group, Table, Text, Tooltip } from '@mantine/core';
import { IconPlugConnectedX } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import getErrorMessage from '../../../helpers/getErrorMessage';
import useFetch from '../../../helpers/useFetch';
import ConfirmationModal from '../../Modals/ConfirmationModal';
import ErrorMessage from '../../ErrorMessage';
import Spinner from '../../Spinner';

const UNNAMED_APP = 'An unnamed app';

const SettingsTabConnectedApps = ({ accountId }) => {
  const fetch = useFetch();

  const [grants, setGrants] = useState();
  const [error, setError] = useState();
  const [confirmRevoke, setConfirmRevoke] = useState(null);
  const [revoking, setRevoking] = useState(false);
  const [revokeError, setRevokeError] = useState();

  const baseUrl = `/api/admin/accounts/${accountId}/oauth/grants`;

  useEffect(() => {
    fetch(baseUrl)
      .then(({ grants }) => setGrants(grants))
      .catch(setError);
  }, [baseUrl, fetch]);

  const handleRevoke = (close) => {
    setRevoking(true);
    setRevokeError(undefined);
    fetch(`${baseUrl}/${confirmRevoke.id}/revoke`, { method: 'POST' })
      .then(({ grant }) => {
        if (!grant) {
          throw new Error('Unexpected response from the server.');
        }
        setGrants((prev) => prev.map((g) => (g.id === grant.id ? grant : g)));
        close();
      })
      .catch((err) => setRevokeError(new Error(getErrorMessage(err))))
      .finally(() => setRevoking(false));
  };

  if (error) {
    return <ErrorMessage message={getErrorMessage(error)} />;
  }

  if (!grants) {
    return <Spinner />;
  }

  if (grants.length === 0) {
    return (
      <Text c="dimmed" size="sm">
        No AI assistants are connected to this account yet.
      </Text>
    );
  }

  return (
    <>
      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>App</Table.Th>
            <Table.Th>Connected by</Table.Th>
            <Table.Th>Stores</Table.Th>
            <Table.Th>Permissions</Table.Th>
            <Table.Th>Connected on</Table.Th>
            <Table.Th>Last used</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {grants.map((grant) => (
            <Table.Tr key={grant.id}>
              <Table.Td>
                <Group gap="xs" wrap="nowrap">
                  <Avatar src={grant.client.logoUri} alt={grant.client.name || UNNAMED_APP} size="sm" radius="sm" />
                  <Text size="sm" fw={500}>
                    {grant.client.name || UNNAMED_APP}
                  </Text>
                </Group>
              </Table.Td>
              <Table.Td>{grant.user.email}</Table.Td>
              <Table.Td>{grant.domains ? grant.domains.map((d) => d.name).join(', ') : 'All stores'}</Table.Td>
              <Table.Td>
                <Group gap={4}>
                  {grant.scopes.map((scope) => (
                    <Badge key={scope} size="xs" variant="light" color="gray">
                      {scope}
                    </Badge>
                  ))}
                </Group>
              </Table.Td>
              <Table.Td>{new Date(grant.createdAt).toLocaleDateString()}</Table.Td>
              <Table.Td>{grant.lastUsedAt ? new Date(grant.lastUsedAt).toLocaleDateString() : 'Never'}</Table.Td>
              <Table.Td>
                {grant.revokedAt ? (
                  <Badge variant="light" color="red">
                    Revoked
                  </Badge>
                ) : (
                  <Badge variant="light" color="green">
                    Active
                  </Badge>
                )}
              </Table.Td>
              <Table.Td>
                {!grant.revokedAt && (
                  <Tooltip label="Disconnect">
                    <ActionIcon
                      color="red"
                      variant="subtle"
                      aria-label={`Disconnect ${grant.client.name || UNNAMED_APP}`}
                      onClick={() => setConfirmRevoke(grant)}
                    >
                      <IconPlugConnectedX size={14} />
                    </ActionIcon>
                  </Tooltip>
                )}
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>

      <ConfirmationModal
        show={confirmRevoke !== null}
        close={() => {
          setConfirmRevoke(null);
          setRevokeError(undefined);
        }}
        confirm={handleRevoke}
        loading={revoking}
        error={revokeError}
        message={
          confirmRevoke && `${confirmRevoke.client.name || UNNAMED_APP} will lose access to this account immediately.`
        }
      />
    </>
  );
};

export default SettingsTabConnectedApps;
